/**
 * Project Config Gear (V2)
 *
 * Adds a floating gear button to the canvas toolbox that surfaces the
 * project-level settings the user chose on the landing page:
 *
 *   - Style — opens the shared `StyleLibraryModal`; the picked style
 *     becomes the project default for newly-created nodes.
 *   - Aspect Ratio — form radio controls (`16:9` / `9:16`); the picked
 *     value becomes the project default for newly-created nodes.
 *
 * Existing canvas nodes are not modified: their per-node `in_*` values
 * already live in YDoc, and only the workflow-state defaults change.
 *
 * Feature tag: `canvas-project-config`. NAD trace command:
 *
 *     scripts/nad trace --feature canvas-project-config --last 10m
 */

import { memo, useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { Check, RectangleHorizontal, RectangleVertical, Settings } from 'lucide-react'
import { toast } from 'sonner'
import { cn } from '../../lib/utils'
import { useProjectConfig, useUpdateProjectConfig } from '../../api/hooks'
import { useAuthOptional } from '../../contexts/authContextRaw'
import { useProjectId } from '../../contexts/ProjectIdContext'
import { useActiveCanvasId } from '../../contexts/ActiveCanvasIdContext'
import type { VideoResolution } from '../../api/types'
import {
  CONTENT_LANGUAGE_AUTO,
  VIDEO_RESOLUTION_OPTIONS,
  buildContentLanguageOptions,
} from '../../i18n/contentLanguages'
import { ASPECT_RATIOS, type AspectRatio } from '../landing/hero-content'
import { StyleLibraryModal } from './nodes/shared/StyleLibraryModal'
import { useStyleLabel } from '../../hooks/useStyleLabel'
import { createNadDebug } from '../../utils/debugLog'
import { canSeeMetaOnlyUi } from '../../utils/metaEmail'
import DirectorModelSettingsSection from '../settings/DirectorModelSettingsSection'
import GatewaySettingsSection from '../settings/GatewaySettingsSection'
import Modal from '../ui/Modal'
import { PreferredVideoModelsSection } from './PreferredVideoModelsSection'
import { PreferredImageModelSection } from './PreferredImageModelSection'
import { registerProjectSettingsOpener } from './projectSettingsEvents'
import {
  CANVAS_PERFORMANCE_SETTING_KEY,
  settingCategory,
  trackProjectSettingChanged,
} from '../../analytics/chatHeaderAnalytics'
import {
  DEFAULT_AUTO_CONFIRM_COST_SETTINGS,
  type AutoConfirmCostSettings,
} from '../chat/autoConfirmCost'
import {
  DEFAULT_CANVAS_PERFORMANCE_SETTINGS,
  type CanvasPerformanceSettings,
} from './canvasPerformanceSettings'

const log = createNadDebug('canvas-project-config')
const settingsLog = createNadDebug('canvas-settings-consolidation')

interface ProjectConfigGearProps {
  /** Reuse the same auto-save settings in the simplified Overview pane. */
  inline?: boolean
  autoConfirmCostSettings?: AutoConfirmCostSettings
  onAutoConfirmImageCostChange?: (value: boolean) => void
  onAutoConfirmVideoCostChange?: (value: boolean) => void
  taskCompletionChimeEnabled?: boolean
  onTaskCompletionChimeEnabledChange?: (value: boolean) => void
  canvasPerformanceSettings?: CanvasPerformanceSettings
  onCanvasPerformanceSettingChange?: (
    key: keyof CanvasPerformanceSettings,
    value: boolean,
  ) => void
  /** Called when the toolbox gear button opens the settings modal. */
  onOpen?: () => void
}

interface SettingsSectionProps {
  title: string
  description?: string
  children: ReactNode
}

function ProjectSettingsSurface({ inline, isOpen, onClose, title, children }: {
  inline: boolean
  isOpen: boolean
  onClose: () => void
  title: string
  children: ReactNode
}) {
  if (inline) return (
    <section className="mx-auto w-full max-w-4xl rounded-xl border border-border-subtle bg-surface-1 p-4" aria-label={title}>
      <h2 className="mb-4 text-lg font-medium text-foreground">{title}</h2>
      {children}
    </section>
  )
  return <Modal isOpen={isOpen} onClose={onClose} title={title} size="lg">{children}</Modal>
}

function SettingsSection({ title, description, children }: SettingsSectionProps) {
  return (
    <section className="space-y-2">
      <div className="space-y-0.5">
        <h3 className="text-xxs font-normal uppercase tracking-wider text-muted-foreground">{title}</h3>
        {description && <p className="text-xxs text-text-muted">{description}</p>}
      </div>
      {children}
    </section>
  )
}

interface SettingsToggleProps {
  testId: string
  label: string
  description?: string
  checked: boolean
  disabled?: boolean
  onChange: (enabled: boolean) => void
}

function SettingsToggle({
  testId,
  label,
  description,
  checked,
  disabled,
  onChange,
}: SettingsToggleProps) {
  return (
    <label
      className={cn(
        'flex items-start gap-3 rounded-lg border border-input-border bg-input-bg px-3 py-2.5 text-sm',
        'transition-colors hover:bg-surface-3 hover:border-input-border-hover',
        disabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer',
      )}
    >
      <input
        data-testid={testId}
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={(event) => onChange(event.target.checked)}
        className="mt-0.5 w-3.5 h-3.5 rounded border-input-border bg-input-bg text-primary focus:ring-2 focus:ring-primary/20 focus:ring-offset-0 cursor-pointer accent-primary disabled:cursor-not-allowed"
      />
      <span className="min-w-0 flex-1">
        <span className="block text-xs font-medium text-foreground">{label}</span>
        {description && (
          <span className="mt-0.5 block text-xxs text-muted-foreground">{description}</span>
        )}
      </span>
    </label>
  )
}

export const ProjectConfigGear = memo(function ProjectConfigGear({
  inline = false,
  autoConfirmCostSettings = DEFAULT_AUTO_CONFIRM_COST_SETTINGS,
  onAutoConfirmImageCostChange,
  onAutoConfirmVideoCostChange,
  taskCompletionChimeEnabled = true,
  onTaskCompletionChimeEnabledChange,
  canvasPerformanceSettings = DEFAULT_CANVAS_PERFORMANCE_SETTINGS,
  onCanvasPerformanceSettingChange,
  onOpen,
}: ProjectConfigGearProps) {
  const { t } = useTranslation('canvas')
  const projectId = useProjectId()
  // Config is per-canvas: read + write the ACTIVE canvas so the gear reflects and
  // edits the canvas the user is on (not always the default "agent" canvas). The
  // ref lets the memoized write handlers read the latest canvas without adding it
  // to each dep array.
  const activeCanvasId = useActiveCanvasId()
  const activeCanvasIdRef = useRef(activeCanvasId)
  activeCanvasIdRef.current = activeCanvasId
  const { data: config } = useProjectConfig(projectId, activeCanvasId)
  const { mutateAsync: updateConfig } = useUpdateProjectConfig()
  const auth = useAuthOptional()
  const canUseMetaOnlyFeatures = canSeeMetaOnlyUi(auth?.user)
  const [isOpen, setIsOpen] = useState(false)
  const [isStyleLibraryOpen, setIsStyleLibraryOpen] = useState(false)
  const [isGatewayModalOpen, setIsGatewayModalOpen] = useState(false)
  const [isDirectorModelSectionVisible, setIsDirectorModelSectionVisible] = useState(false)

  const logSettingsVisibility = useCallback((open: boolean, source: string) => {
    settingsLog({
      event: open ? 'canvas-settings-panel-opened' : 'canvas-settings-panel-closed',
      trace_id: `canvas-settings:${projectId}:toolbox`,
      project_id: projectId,
      source: 'toolbox',
      trigger: source,
      component: 'ProjectConfigGear',
      has_project_config: !!config,
      style_id: config?.style_id ?? null,
      aspect_ratio: config?.aspect_ratio ?? null,
      remove_watermark: config?.remove_watermark ?? null,
    })
  }, [config, projectId])

  const handleOpenSettings = useCallback(() => {
    log('gear:open', { has_config: !!config })
    logSettingsVisibility(true, 'toolbox')
    onOpen?.()
    setIsOpen(true)
  }, [config, logSettingsVisibility, onOpen])

  // Chat config cards (project-setup card, global-config action card) open
  // this modal via the settings-events bridge. Only non-inline gears own a
  // modal; the registry lets just the most recently mounted one respond.
  // Latest-value ref keeps the registration stable (mount order decides which
  // gear responds; re-registering on every config change would reshuffle it).
  const logSettingsVisibilityRef = useRef(logSettingsVisibility)
  logSettingsVisibilityRef.current = logSettingsVisibility
  useEffect(() => {
    if (inline) return undefined
    return registerProjectSettingsOpener((source) => {
      log('gear:open', { source })
      logSettingsVisibilityRef.current(true, source)
      setIsOpen(true)
    })
  }, [inline])

  const handleCloseSettings = useCallback((source = 'modal') => {
    if (isStyleLibraryOpen || isGatewayModalOpen) {
      log('gear:close_suppressed:nested_modal', {
        source,
        style_library_open: isStyleLibraryOpen,
        gateway_modal_open: isGatewayModalOpen,
      })
      settingsLog({
        event: 'canvas-settings-close-suppressed-nested-modal',
        trace_id: `canvas-settings:${projectId}:toolbox`,
        project_id: projectId,
        source: 'toolbox',
        trigger: source,
        component: 'ProjectConfigGear',
        style_library_open: isStyleLibraryOpen,
        gateway_modal_open: isGatewayModalOpen,
      })
      return
    }
    log('gear:close', { source })
    logSettingsVisibility(false, source)
    setIsOpen(false)
  }, [isGatewayModalOpen, isStyleLibraryOpen, logSettingsVisibility, projectId])

  const handleToggle = useCallback(() => {
    if (isOpen) {
      handleCloseSettings('toolbox')
    } else {
      handleOpenSettings()
    }
  }, [handleCloseSettings, handleOpenSettings, isOpen])

  // Project Settings is an auto-save panel: every control persists immediately
  // on change. Surface a toast on each save so the user gets confirmation. From
  // the user's perspective there is no backend-vs-localStorage distinction, so
  // every control confirms consistently. A per-setting toast id keeps concurrent
  // saves of different settings from overwriting each other's result, while a
  // retry of the same setting replaces its own prior toast.
  const notifySaved = useCallback(
    (settingKey: string) => {
      toast.success(t('projectConfigGear.saved'), { id: `project-config-save:${settingKey}` })
    },
    [t],
  )

  // Guards toast accuracy when the same setting is changed again before its
  // previous (async) save resolves: each save start bumps a per-setting
  // sequence, and only the completion that is still the latest attempt is
  // allowed to toast. Prevents an older success/failure from overwriting a
  // newer one on the shared per-setting toast id. (Synchronous localStorage
  // toggles don't race, so they don't use this.)
  const saveAttemptRef = useRef<Record<string, number>>({})
  const beginSaveAttempt = useCallback((settingKey: string) => {
    const next = (saveAttemptRef.current[settingKey] ?? 0) + 1
    saveAttemptRef.current[settingKey] = next
    return next
  }, [])
  const isLatestSaveAttempt = useCallback(
    (settingKey: string, attempt: number) => saveAttemptRef.current[settingKey] === attempt,
    [],
  )
  // Backend (async) save result → toast, but only if this is still the latest
  // attempt for the setting (see saveAttemptRef).
  const notifyBackendSaved = useCallback(
    (settingKey: string, attempt: number) => {
      if (isLatestSaveAttempt(settingKey, attempt)) notifySaved(settingKey)
    },
    [isLatestSaveAttempt, notifySaved],
  )
  const notifyBackendError = useCallback(
    (settingKey: string, attempt: number, messageKey: string, message: string) => {
      if (!isLatestSaveAttempt(settingKey, attempt)) return
      toast.error(t(messageKey, { message }), { id: `project-config-save:${settingKey}` })
    },
    [isLatestSaveAttempt, t],
  )

  const handleAutoConfirmImageChange = useCallback(
    (enabled: boolean) => {
      // The persistence callback is optional; when absent nothing is saved, so
      // both the analytics event and the toast must reflect that.
      const persisted = !!onAutoConfirmImageCostChange
      settingsLog({
        event: 'canvas-setting-auto-confirm-image-changed',
        trace_id: `canvas-settings:${projectId}:auto-confirm-image`,
        project_id: projectId,
        source: 'toolbox',
        component: 'ProjectConfigGear',
        setting_key: 'auto_confirm_image_costs',
        previous_value: autoConfirmCostSettings.image,
        next_value: enabled,
        persisted,
        persistence_scope: 'local_storage',
        persistence_result: persisted ? 'saved' : 'skipped',
      })
      onAutoConfirmImageCostChange?.(enabled)
      if (persisted) {
        notifySaved('auto_confirm_image_costs')
        trackProjectSettingChanged({
          projectId,
          settingKey: 'auto_confirm_image_costs',
          next: settingCategory.toggle(enabled),
          previous: settingCategory.toggle(autoConfirmCostSettings.image),
        })
      }
    },
    [autoConfirmCostSettings.image, onAutoConfirmImageCostChange, projectId, notifySaved],
  )

  const handleAutoConfirmVideoChange = useCallback(
    (enabled: boolean) => {
      const persisted = !!onAutoConfirmVideoCostChange
      settingsLog({
        event: 'canvas-setting-auto-confirm-video-changed',
        trace_id: `canvas-settings:${projectId}:auto-confirm-video`,
        project_id: projectId,
        source: 'toolbox',
        component: 'ProjectConfigGear',
        setting_key: 'auto_confirm_video_costs',
        previous_value: autoConfirmCostSettings.video,
        next_value: enabled,
        persisted,
        persistence_scope: 'local_storage',
        persistence_result: persisted ? 'saved' : 'skipped',
      })
      onAutoConfirmVideoCostChange?.(enabled)
      if (persisted) {
        notifySaved('auto_confirm_video_costs')
        trackProjectSettingChanged({
          projectId,
          settingKey: 'auto_confirm_video_costs',
          next: settingCategory.toggle(enabled),
          previous: settingCategory.toggle(autoConfirmCostSettings.video),
        })
      }
    },
    [autoConfirmCostSettings.video, onAutoConfirmVideoCostChange, projectId, notifySaved],
  )

  const handleCanvasPerformanceChange = useCallback(
    (key: keyof CanvasPerformanceSettings, enabled: boolean) => {
      const persisted = !!onCanvasPerformanceSettingChange
      settingsLog({
        event: 'canvas-performance-setting-changed',
        trace_id: `canvas-settings:${projectId}:${key}`,
        project_id: projectId,
        source: 'toolbox',
        component: 'ProjectConfigGear',
        setting_key: key,
        previous_value: canvasPerformanceSettings[key],
        next_value: enabled,
        persisted,
        persistence_scope: 'local_storage',
        persistence_result: persisted ? 'saved' : 'skipped',
      })
      onCanvasPerformanceSettingChange?.(key, enabled)
      if (persisted) {
        notifySaved(key)
        trackProjectSettingChanged({
          projectId,
          settingKey: CANVAS_PERFORMANCE_SETTING_KEY[key],
          next: settingCategory.toggle(enabled),
          previous: settingCategory.toggle(canvasPerformanceSettings[key]),
        })
      }
    },
    [canvasPerformanceSettings, onCanvasPerformanceSettingChange, projectId, notifySaved],
  )

  const handleTaskCompletionChimeChange = useCallback(
    (enabled: boolean) => {
      const persisted = !!onTaskCompletionChimeEnabledChange
      settingsLog({
        event: 'canvas-setting-task-completion-chime-changed',
        trace_id: `canvas-settings:${projectId}:task-completion-chime`,
        project_id: projectId,
        source: 'toolbox',
        component: 'ProjectConfigGear',
        setting_key: 'task_completion_chime',
        previous_value: taskCompletionChimeEnabled,
        next_value: enabled,
        persisted,
        persistence_scope: 'local_storage',
        persistence_result: persisted ? 'saved' : 'skipped',
      })
      onTaskCompletionChimeEnabledChange?.(enabled)
      if (persisted) {
        notifySaved('task_completion_chime')
        trackProjectSettingChanged({
          projectId,
          settingKey: 'task_completion_chime',
          next: settingCategory.toggle(enabled),
          previous: settingCategory.toggle(taskCompletionChimeEnabled),
        })
      }
    },
    [onTaskCompletionChimeEnabledChange, projectId, taskCompletionChimeEnabled, notifySaved],
  )

  const handleRemoveWatermarkChange = useCallback(
    async (enabled: boolean) => {
      const previous = config?.remove_watermark ?? true
      settingsLog({
        event: 'canvas-setting-remove-watermark-changed',
        trace_id: `canvas-settings:${projectId}:remove-watermark`,
        project_id: projectId,
        source: 'toolbox',
        component: 'ProjectConfigGear',
        setting_key: 'remove_watermark',
        previous_value: previous,
        next_value: enabled,
        persisted: true,
        persistence_scope: 'workflow_state',
        persistence_result: 'pending',
      })
      const saveAttempt = beginSaveAttempt('remove_watermark')
      try {
        const next = await updateConfig({ projectId, canvasId: activeCanvasIdRef.current, data: { remove_watermark: enabled } })
        settingsLog({
          event: 'canvas-setting-remove-watermark-saved',
          trace_id: `canvas-settings:${projectId}:remove-watermark`,
          project_id: projectId,
          source: 'toolbox',
          component: 'ProjectConfigGear',
          setting_key: 'remove_watermark',
          previous_value: previous,
          next_value: next.remove_watermark,
          persisted: true,
          persistence_scope: 'workflow_state',
          persistence_result: 'saved',
        })
        notifyBackendSaved('remove_watermark', saveAttempt)
        trackProjectSettingChanged({
          projectId,
          settingKey: 'remove_watermark',
          next: settingCategory.toggle(next.remove_watermark ?? enabled),
          previous: settingCategory.toggle(previous),
        })
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Unknown error'
        settingsLog({
          event: 'canvas-setting-remove-watermark-save-failed',
          trace_id: `canvas-settings:${projectId}:remove-watermark`,
          project_id: projectId,
          source: 'toolbox',
          component: 'ProjectConfigGear',
          setting_key: 'remove_watermark',
          previous_value: previous,
          next_value: enabled,
          persisted: false,
          persistence_scope: 'workflow_state',
          persistence_result: 'error',
          error: message,
        })
        notifyBackendError('remove_watermark', saveAttempt, 'projectConfigGear.failedToUpdateWatermark', message)
      }
    },
    [config?.remove_watermark, projectId, updateConfig, beginSaveAttempt, notifyBackendSaved, notifyBackendError],
  )

  const handleOpenStyleLibrary = useCallback(() => {
    log('style:modal_open', { current_style_id: config?.style_id ?? null })
    setIsStyleLibraryOpen(true)
  }, [config?.style_id])

  const handleApplyStyle = useCallback(
    async (styleId: string) => {
      const previous = config?.style_id
      log('style:apply_request', { previous, next: styleId })
      setIsStyleLibraryOpen(false)
      const saveAttempt = beginSaveAttempt('style_id')
      try {
        const next = await updateConfig({ projectId, canvasId: activeCanvasIdRef.current, data: { style_id: styleId } })
        log('style:apply_success', { previous, next: next.style_id })
        notifyBackendSaved('style_id', saveAttempt)
        trackProjectSettingChanged({
          projectId,
          settingKey: 'style',
          next: settingCategory.style(next.style_id),
          previous: settingCategory.style(previous),
        })
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Unknown error'
        log('style:apply_error', { previous, attempted: styleId, error: message })
        notifyBackendError('style_id', saveAttempt, 'projectConfigGear.failedToUpdateStyle', message)
      }
    },
    [projectId, config?.style_id, updateConfig, beginSaveAttempt, notifyBackendSaved, notifyBackendError],
  )

  const handleApplyAspect = useCallback(
    async (aspect: AspectRatio) => {
      const previous = config?.aspect_ratio
      if (previous === aspect) return
      log('aspect:apply_request', { previous, next: aspect })
      settingsLog({
        event: 'canvas-setting-aspect-ratio-changed',
        trace_id: `canvas-settings:${projectId}:aspect-ratio`,
        project_id: projectId,
        source: 'toolbox',
        component: 'ProjectConfigGear',
        setting_key: 'aspect_ratio',
        previous_value: previous ?? null,
        next_value: aspect,
        persisted: true,
        persistence_scope: 'workflow_state',
        persistence_result: 'pending',
      })
      const saveAttempt = beginSaveAttempt('aspect_ratio')
      try {
        const next = await updateConfig({ projectId, canvasId: activeCanvasIdRef.current, data: { aspect_ratio: aspect } })
        log('aspect:apply_success', { previous, next: next.aspect_ratio })
        settingsLog({
          event: 'canvas-setting-aspect-ratio-saved',
          trace_id: `canvas-settings:${projectId}:aspect-ratio`,
          project_id: projectId,
          source: 'toolbox',
          component: 'ProjectConfigGear',
          setting_key: 'aspect_ratio',
          previous_value: previous ?? null,
          next_value: next.aspect_ratio,
          persisted: true,
          persistence_scope: 'workflow_state',
          persistence_result: 'saved',
        })
        notifyBackendSaved('aspect_ratio', saveAttempt)
        trackProjectSettingChanged({
          projectId,
          settingKey: 'aspect_ratio',
          next: settingCategory.aspectRatio(next.aspect_ratio),
          previous: settingCategory.aspectRatio(previous),
        })
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Unknown error'
        log('aspect:apply_error', { previous, attempted: aspect, error: message })
        settingsLog({
          event: 'canvas-setting-aspect-ratio-save-failed',
          trace_id: `canvas-settings:${projectId}:aspect-ratio`,
          project_id: projectId,
          source: 'toolbox',
          component: 'ProjectConfigGear',
          setting_key: 'aspect_ratio',
          previous_value: previous ?? null,
          next_value: aspect,
          persisted: false,
          persistence_scope: 'workflow_state',
          persistence_result: 'error',
          error: message,
        })
        notifyBackendError('aspect_ratio', saveAttempt, 'projectConfigGear.failedToUpdateAspectRatio', message)
      }
    },
    [projectId, config?.aspect_ratio, updateConfig, beginSaveAttempt, notifyBackendSaved, notifyBackendError],
  )

  const handleApplyContentLanguage = useCallback(
    // `choice` is 'auto' (reset to Auto/null) or a concrete TTS-supported content
    // language code. Axis 3: the language of GENERATED content
    // (scripts/dialogue/narration), independent of the UI locale (axis 1) and the
    // Director conversation language (axis 2).
    async (choice: string) => {
      const previous = config?.content_language ?? null
      log('content_language:apply_request', { previous, next: choice })
      settingsLog({
        event: 'canvas-setting-content-language-changed',
        trace_id: `canvas-settings:${projectId}:content-language`,
        project_id: projectId,
        source: 'toolbox',
        component: 'ProjectConfigGear',
        setting_key: 'content_language',
        previous_value: previous,
        next_value: choice,
        persisted: true,
        persistence_scope: 'workflow_state',
        persistence_result: 'pending',
      })
      const saveAttempt = beginSaveAttempt('content_language')
      try {
        const next = await updateConfig({ projectId, canvasId: activeCanvasIdRef.current, data: { content_language: choice } })
        log('content_language:apply_success', {
          previous,
          next: next.content_language ?? null,
        })
        settingsLog({
          event: 'canvas-setting-content-language-saved',
          trace_id: `canvas-settings:${projectId}:content-language`,
          project_id: projectId,
          source: 'toolbox',
          component: 'ProjectConfigGear',
          setting_key: 'content_language',
          previous_value: previous,
          next_value: next.content_language ?? null,
          persisted: true,
          persistence_scope: 'workflow_state',
          persistence_result: 'saved',
        })
        notifyBackendSaved('content_language', saveAttempt)
        trackProjectSettingChanged({
          projectId,
          settingKey: 'content_language',
          next: settingCategory.contentLanguage(next.content_language),
          previous: settingCategory.contentLanguage(previous),
        })
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Unknown error'
        log('content_language:apply_error', { previous, attempted: choice, error: message })
        settingsLog({
          event: 'canvas-setting-content-language-save-failed',
          trace_id: `canvas-settings:${projectId}:content-language`,
          project_id: projectId,
          source: 'toolbox',
          component: 'ProjectConfigGear',
          setting_key: 'content_language',
          previous_value: previous,
          next_value: choice,
          persisted: false,
          persistence_scope: 'workflow_state',
          persistence_result: 'error',
          error: message,
        })
        notifyBackendError('content_language', saveAttempt, 'projectConfigGear.failedToUpdateContentLanguage', message)
      }
    },
    [projectId, config?.content_language, updateConfig, beginSaveAttempt, notifyBackendSaved, notifyBackendError],
  )

  const handleApplyResolution = useCallback(
    async (resolution: VideoResolution) => {
      const previous = config?.video_resolution
      if (previous === resolution) return
      log('output_quality:apply_request', { previous, next: resolution })
      settingsLog({
        event: 'canvas-setting-output-quality-changed',
        trace_id: `canvas-settings:${projectId}:output-quality`,
        project_id: projectId,
        source: 'toolbox',
        component: 'ProjectConfigGear',
        setting_key: 'video_resolution',
        previous_value: previous ?? null,
        next_value: resolution,
        persisted: true,
        persistence_scope: 'workflow_state',
        persistence_result: 'pending',
      })
      const saveAttempt = beginSaveAttempt('video_resolution')
      try {
        const next = await updateConfig({ projectId, canvasId: activeCanvasIdRef.current, data: { video_resolution: resolution } })
        log('output_quality:apply_success', { previous, next: next.video_resolution })
        settingsLog({
          event: 'canvas-setting-output-quality-saved',
          trace_id: `canvas-settings:${projectId}:output-quality`,
          project_id: projectId,
          source: 'toolbox',
          component: 'ProjectConfigGear',
          setting_key: 'video_resolution',
          previous_value: previous ?? null,
          next_value: next.video_resolution,
          persisted: true,
          persistence_scope: 'workflow_state',
          persistence_result: 'saved',
        })
        notifyBackendSaved('video_resolution', saveAttempt)
        trackProjectSettingChanged({
          projectId,
          settingKey: 'video_resolution',
          next: settingCategory.videoResolution(next.video_resolution),
          previous: settingCategory.videoResolution(previous),
        })
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Unknown error'
        log('output_quality:apply_error', { previous, attempted: resolution, error: message })
        settingsLog({
          event: 'canvas-setting-output-quality-save-failed',
          trace_id: `canvas-settings:${projectId}:output-quality`,
          project_id: projectId,
          source: 'toolbox',
          component: 'ProjectConfigGear',
          setting_key: 'video_resolution',
          previous_value: previous ?? null,
          next_value: resolution,
          persisted: false,
          persistence_scope: 'workflow_state',
          persistence_result: 'error',
          error: message,
        })
        notifyBackendError('video_resolution', saveAttempt, 'projectConfigGear.failedToUpdateOutputQuality', message)
      }
    },
    [projectId, config?.video_resolution, updateConfig, beginSaveAttempt, notifyBackendSaved, notifyBackendError],
  )

  const handlePreferredVideoModelsChange = useCallback(
    async (next: string[]) => {
      const previous = config?.preferred_video_models ?? []
      const saveAttempt = beginSaveAttempt('preferred_video_models')
      settingsLog({
        event: 'canvas-setting-preferred-video-models-changed',
        trace_id: `canvas-settings:${projectId}:preferred-video-models`,
        project_id: projectId,
        source: 'toolbox',
        component: 'ProjectConfigGear',
        setting_key: 'preferred_video_models',
        previous_value: previous.join(','),
        next_value: next.join(','),
        persisted: true,
        persistence_scope: 'workflow_state',
        persistence_result: 'pending',
      })
      try {
        const saved = await updateConfig({
          projectId,
          canvasId: activeCanvasIdRef.current,
          data: { preferred_video_models: next },
        })
        log('preferred_video_models:apply_success', {
          previous: previous.join(','),
          next: saved.preferred_video_models.join(','),
          effective_default: saved.effective_default_video_model,
        })
        notifyBackendSaved('preferred_video_models', saveAttempt)
        trackProjectSettingChanged({
          projectId,
          settingKey: 'preferred_video_models',
          next: settingCategory.modelList(saved.preferred_video_models),
          previous: settingCategory.modelList(previous),
        })
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Unknown error'
        log('preferred_video_models:apply_error', { attempted: next.join(','), error: message })
        notifyBackendError(
          'preferred_video_models',
          saveAttempt,
          'projectConfigGear.failedToUpdatePreferredVideoModels',
          message,
        )
      }
    },
    [projectId, config?.preferred_video_models, updateConfig, beginSaveAttempt, notifyBackendSaved, notifyBackendError],
  )

  const handlePreferredImageModelChange = useCallback(async (next: string) => {
    const saveAttempt = beginSaveAttempt('preferred_image_model')
    try {
      await updateConfig({ projectId, canvasId: activeCanvasIdRef.current, data: { preferred_image_model: next } })
      settingsLog({ event: 'image_model_preference_saved', feature: 'preferred-image-model', project_id: projectId, previous: config?.preferred_image_model ?? 'auto', selected: next })
      notifyBackendSaved('preferred_image_model', saveAttempt)
      trackProjectSettingChanged({
        projectId,
        settingKey: 'preferred_image_model',
        next: settingCategory.model(next),
        previous: settingCategory.model(config?.preferred_image_model),
      })
    } catch (err) {
      notifyBackendError('preferred_image_model', saveAttempt, 'projectConfigGear.failedToUpdatePreferredImageModel', err instanceof Error ? err.message : String(err))
    }
  }, [projectId, config?.preferred_image_model, updateConfig, beginSaveAttempt, notifyBackendSaved, notifyBackendError])

  const { label: styleLabel } = useStyleLabel(config?.style_id)

  const translatedResolutionOptions = useMemo(
    () =>
      VIDEO_RESOLUTION_OPTIONS.map((option) => ({
        ...option,
        description: t(`projectConfigGear.outputQuality_${option.value}`),
      })),
    [t],
  )

  // Content-language dropdown options. The Auto entry is translated per UI
  // locale (shared:contentLanguageAuto). Concrete languages use a hybrid
  // "<UI-language name> (<endonym>)" label — see `buildContentLanguageOptions`,
  // the single source shared with the landing project-plan modal so the two
  // never drift. ~30 languages → a native <select> (type-ahead), not a radio.
  const contentLanguageOptions = useMemo(() => buildContentLanguageOptions(t), [t])

  // Persisted value drives the select; null/absent → Auto sentinel.
  const contentLanguageValue = config?.content_language ?? CONTENT_LANGUAGE_AUTO

  const AspectIcon = config?.aspect_ratio === '9:16' ? RectangleVertical : RectangleHorizontal

  return (
    <>
      {!inline && (
      <div className="relative">
        <button
          onClick={handleToggle}
          aria-haspopup="dialog"
          aria-expanded={isOpen}
          aria-controls={isOpen ? 'project-settings-modal' : undefined}
          // Same class string as AppHeader's `iconButton` — this gear sits in
          // that row and has to match it. Also rendered in CanvasToolbox, whose
          // other dock buttons are already neutral, so `chrome` is the more
          // consistent value in both places.
          className={cn(
            'flex h-9 w-9 items-center justify-center rounded-lg text-chrome/70',
            'hover:bg-chrome/10 hover:text-chrome transition-colors cursor-pointer',
          )}
          title={t('projectConfigGear.projectSettings')}
          data-testid="toolbox-project-config"
        >
          <Settings className="h-4 w-4" />
        </button>
      </div>
      )}

      <ProjectSettingsSurface inline={inline} isOpen={isOpen} onClose={handleCloseSettings} title={t('projectConfigGear.projectSettingsTitle')}>
        <form
          id="project-settings-modal"
          data-testid="project-settings-modal"
          data-legacy-testid="project-config-popover"
          onSubmit={(event) => {
            event.preventDefault()
            // The Done button lives in a pinned (shrink-0) footer sibling of the
            // scroll region, so it is reachable at any scroll position. Log the
            // scroll state at click time as evidence the footer stayed visible.
            const scroller = event.currentTarget.querySelector<HTMLElement>(
              '[data-testid="project-config-popover"]',
            )
            log({
              event: 'done_footer:submit',
              scroll_top: scroller?.scrollTop ?? null,
              scroll_height: scroller?.scrollHeight ?? null,
              client_height: scroller?.clientHeight ?? null,
              scrolled: scroller ? scroller.scrollTop > 0 : null,
            })
            handleCloseSettings('done-button')
          }}
          className={cn('flex flex-col', !inline && 'max-h-[72vh]')}
        >
          <div data-testid="project-config-popover" className="min-h-0 flex-1 space-y-4 overflow-y-auto pr-1">
            <SettingsSection
              title={t('projectConfigGear.projectDefaults')}
              description={t('projectConfigGear.usedByNewNodes')}
            >
            <button
              type="button"
              onClick={handleOpenStyleLibrary}
              disabled={!config}
              data-testid="project-config-style-row"
              className={cn(
                'w-full flex items-center justify-between gap-3 rounded-lg border border-input-border bg-input-bg px-3 py-2.5 text-sm',
                'text-foreground transition-colors hover:bg-surface-3 hover:border-input-border-hover',
                'focus:outline-none focus:ring-2 focus:ring-primary/20',
                'cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed',
              )}
            >
              <span className="min-w-0 text-left">
                <span className="block text-xs font-medium text-foreground">{t('projectConfigGear.style')}</span>
                <span className="block text-xxs text-muted-foreground">{t('projectConfigGear.defaultVisualStyle')}</span>
              </span>
              <span
                className="max-w-64 truncate rounded-md border border-border-subtle bg-surface-1 px-2 py-1 text-xs font-medium text-foreground"
                data-testid="project-config-style-value"
              >
                {styleLabel}
              </span>
            </button>

            <div
              className="rounded-lg border border-input-border bg-input-bg p-3"
              data-testid="project-config-aspect-row"
            >
              <div className="mb-2 flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-xs font-medium text-foreground">{t('projectConfigGear.aspectRatio')}</p>
                  <p className="text-xxs text-muted-foreground">{t('projectConfigGear.defaultFrameForNewMedia')}</p>
                </div>
                <span
                  className="flex items-center gap-1.5 rounded-md border border-border-subtle bg-surface-1 px-2 py-1 text-xs font-medium text-foreground"
                  data-testid="project-config-aspect-value"
                >
                  <AspectIcon className="w-4 h-4" strokeWidth={1.5} aria-hidden="true" />
                  {config?.aspect_ratio ?? '—'}
                </span>
              </div>
              <div
                role="radiogroup"
                aria-label={t('projectConfigGear.aspectRatioAriaLabel')}
                data-testid="project-config-aspect-menu"
                className="grid grid-cols-2 gap-2"
              >
                {ASPECT_RATIOS.map((ratio) => {
                  const Icon = ratio === '9:16' ? RectangleVertical : RectangleHorizontal
                  const checked = config?.aspect_ratio === ratio
                  return (
                    <button
                      key={ratio}
                      type="button"
                      role="radio"
                      aria-checked={checked}
                      disabled={!config}
                      data-testid={`project-config-aspect-option-${ratio.replace(':', '-')}`}
                      onClick={() => handleApplyAspect(ratio)}
                      className={cn(
                        'h-9 rounded-lg border px-3 text-xs font-medium transition-colors',
                        'flex items-center justify-between cursor-pointer disabled:cursor-not-allowed disabled:opacity-50',
                        'focus:outline-none focus:ring-2 focus:ring-primary/20',
                        checked
                          ? 'border-primary bg-surface-2 text-foreground'
                          : 'border-input-border bg-surface-1 text-muted-foreground hover:bg-surface-3 hover:border-input-border-hover hover:text-foreground',
                      )}
                    >
                      <span className="flex items-center gap-1.5">
                        <Icon className="w-3.5 h-3.5" strokeWidth={1.5} aria-hidden="true" />
                        {ratio}
                      </span>
                      {checked && (
                        <Check className="w-3.5 h-3.5 text-primary" aria-hidden="true" />
                      )}
                    </button>
                  )
                })}
              </div>
            </div>

            <div
              className="rounded-lg border border-input-border bg-input-bg p-3"
              data-testid="project-config-content-language-row"
            >
              <div className="flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-xs font-medium text-foreground">
                    {t('projectConfigGear.contentLanguage')}
                  </p>
                  <p className="text-xxs text-muted-foreground">
                    {t('projectConfigGear.contentLanguageNote')}
                  </p>
                </div>
                {/* ~30 TTS-supported languages → a native <select> (browser
                    type-ahead) rather than a radio group. Auto → backend null. */}
                {/* Uses the native select chevron (no custom SVG) so there is no
                    hardcoded color; box styling uses the shared input/surface
                    tokens + the same hover/focus affordance as sibling controls. */}
                <select
                  data-testid="project-config-content-language-select"
                  aria-label={t('projectConfigGear.contentLanguageAriaLabel')}
                  value={contentLanguageValue}
                  disabled={!config}
                  onChange={(event) => handleApplyContentLanguage(event.target.value)}
                  className={cn(
                    'h-9 max-w-48 rounded-lg border border-input-border bg-input-bg px-2 text-xs font-medium text-foreground',
                    'cursor-pointer transition-colors',
                    'hover:bg-surface-3 hover:border-input-border-hover',
                    'focus:outline-none focus:border-input-border-hover focus:ring-2 focus:ring-primary/20',
                    'disabled:opacity-50 disabled:cursor-not-allowed',
                  )}
                >
                  {contentLanguageOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            </SettingsSection>

            <div className="h-px bg-border-subtle" />

            <SettingsSection title={t('projectConfigGear.canvasGrid')}>
              <div className="grid gap-2 md:grid-cols-3">
                <SettingsToggle
                  testId="canvas-snap-to-grid-toggle"
                  label={t('projectConfigGear.snapToGrid')}
                  description={t('projectConfigGear.snapToGridDescription')}
                  checked={canvasPerformanceSettings.snapToGrid}
                  onChange={(enabled) => handleCanvasPerformanceChange('snapToGrid', enabled)}
                />
              </div>
            </SettingsSection>

            <div className="h-px bg-border-subtle" />

            <SettingsSection title={t('projectConfigGear.canvasPerformance')}>
              <div className="grid gap-2 md:grid-cols-3">
                <SettingsToggle
                  testId="canvas-performance-render-visible-elements-toggle"
                  label={t('projectConfigGear.renderVisibleOnly')}
                  checked={canvasPerformanceSettings.renderOnlyVisibleElements}
                  onChange={(enabled) => handleCanvasPerformanceChange('renderOnlyVisibleElements', enabled)}
                />
                <SettingsToggle
                  testId="canvas-performance-lightweight-node-chrome-toggle"
                  label={t('projectConfigGear.lightweightIdleChrome')}
                  description={t('projectConfigGear.idleNodesDescription')}
                  checked={canvasPerformanceSettings.lightweightNodeChrome}
                  onChange={(enabled) => handleCanvasPerformanceChange('lightweightNodeChrome', enabled)}
                />
                <SettingsToggle
                  testId="canvas-performance-always-hide-edges-toggle"
                  label={t('projectConfigGear.alwaysHideEdges')}
                  description={t('projectConfigGear.hideEdgesDescription')}
                  checked={canvasPerformanceSettings.alwaysHideEdges}
                  onChange={(enabled) => handleCanvasPerformanceChange('alwaysHideEdges', enabled)}
                />
              </div>
            </SettingsSection>

            <div className="h-px bg-border-subtle" />

            <SettingsSection title={t('projectConfigGear.generationControls')}>
              <div className="space-y-2">
                <div className="grid gap-2 md:grid-cols-2">
                  <SettingsToggle
                    testId="auto-confirm-image-costs-toggle"
                    label={t('projectConfigGear.autoConfirmImageAudio')}
                    checked={autoConfirmCostSettings.image}
                    onChange={handleAutoConfirmImageChange}
                  />
                  <SettingsToggle
                    testId="auto-confirm-video-costs-toggle"
                    label={t('projectConfigGear.autoConfirmVideo')}
                    checked={autoConfirmCostSettings.video}
                    onChange={handleAutoConfirmVideoChange}
                  />
                  <SettingsToggle
                    testId="task-completion-chime-toggle"
                    label={t('projectConfigGear.taskCompletionChime')}
                    description={t('projectConfigGear.taskCompletionChimeDescription')}
                    checked={taskCompletionChimeEnabled}
                    onChange={handleTaskCompletionChimeChange}
                  />
                  <SettingsToggle
                    testId="remove-watermark-toggle"
                    label={t('projectConfigGear.removeWatermark')}
                    checked={config?.remove_watermark ?? true}
                    disabled={!config}
                    onChange={handleRemoveWatermarkChange}
                  />
                </div>
                <p className="text-xs font-medium leading-5 text-muted-foreground">
                  {t('projectConfigGear.autoConfirmNote')}
                </p>
              </div>
            </SettingsSection>

            <div className="h-px bg-border-subtle" />

            <PreferredImageModelSection
              value={config?.preferred_image_model ?? 'auto'}
              disabled={!config}
              onChange={handlePreferredImageModelChange}
            />
            <div className="h-px bg-border-subtle" />

            <SettingsSection
              title={t('projectConfigGear.videoOutput')}
              description={t('projectConfigGear.videoOutputDescription')}
            >
              <div
                className="rounded-lg border border-input-border bg-input-bg p-3"
                data-testid="project-config-output-quality-row"
              >
                <div className="mb-2 flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-xs font-medium text-foreground">
                      {t('projectConfigGear.outputQuality')}
                    </p>
                    <p className="text-xxs text-muted-foreground">
                      {t('projectConfigGear.outputQualityNote')}
                    </p>
                  </div>
                  <span
                    className="rounded-md border border-border-subtle bg-surface-1 px-2 py-1 text-xs font-medium text-foreground"
                    data-testid="project-config-output-quality-value"
                  >
                    {config?.video_resolution ?? '—'}
                  </span>
                </div>
                <div
                  role="radiogroup"
                  aria-label={t('projectConfigGear.outputQuality')}
                  data-testid="project-config-output-quality-menu"
                  className="grid grid-cols-3 gap-2"
                >
                  {translatedResolutionOptions.map((option) => {
                    const checked = config?.video_resolution === option.value
                    return (
                      <button
                        key={option.value}
                        type="button"
                        role="radio"
                        aria-checked={checked}
                        disabled={!config}
                        data-testid={`project-config-output-quality-option-${option.value}`}
                        onClick={() => handleApplyResolution(option.value)}
                        className={cn(
                          'rounded-lg border px-3 py-2 text-left text-xs font-medium transition-colors',
                          'flex flex-col gap-0.5 cursor-pointer disabled:cursor-not-allowed disabled:opacity-50',
                          'focus:outline-none focus:ring-2 focus:ring-primary/20',
                          checked
                            ? 'border-primary bg-surface-2 text-foreground'
                            : 'border-input-border bg-surface-1 text-muted-foreground hover:bg-surface-3 hover:border-input-border-hover hover:text-foreground',
                        )}
                      >
                        <span className="flex items-center justify-between gap-1.5 text-foreground">
                          {option.label}
                          {checked && (
                            <Check className="w-3.5 h-3.5 text-primary" aria-hidden="true" />
                          )}
                        </span>
                        <span className="text-xxs text-muted-foreground">
                          {option.description}
                        </span>
                      </button>
                    )
                  })}
                </div>
              </div>
              <PreferredVideoModelsSection
                preferred={config?.preferred_video_models ?? []}
                effectiveDefault={config?.effective_default_video_model ?? null}
                effectiveDefaultRunnable={config?.effective_default_video_model_runnable ?? true}
                disabled={!config}
                onChange={handlePreferredVideoModelsChange}
              />
            </SettingsSection>

            <div className="h-px bg-border-subtle" />

            {canUseMetaOnlyFeatures && (
              <DirectorModelSettingsSection
                onVisibilityChange={setIsDirectorModelSectionVisible}
                analyticsEntryPoint="project_settings"
              />
            )}

            {canUseMetaOnlyFeatures && isDirectorModelSectionVisible && (
              <div className="h-px bg-border-subtle" />
            )}

            <SettingsSection title={t('projectConfigGear.myVideoModel')}>
              <div className="rounded-lg border border-input-border bg-input-bg p-3">
                <GatewaySettingsSection
                  showTitle={false}
                  onModalOpenChange={setIsGatewayModalOpen}
                  analyticsEntryPoint="project_settings"
                />
              </div>
            </SettingsSection>

            {canUseMetaOnlyFeatures && (
              <>
                <div className="h-px bg-border-subtle" />

                <SettingsSection title={t('projectConfigGear.myImageModel')}>
                  <div className="rounded-lg border border-input-border bg-input-bg p-3">
                    <GatewaySettingsSection
                      showTitle={false}
                      modelKind="image"
                      onModalOpenChange={setIsGatewayModalOpen}
                      analyticsEntryPoint="project_settings"
                    />
                  </div>
                </SettingsSection>
              </>
            )}
          </div>

          {!inline && <div className="flex shrink-0 items-center justify-end gap-2 border-t border-border-subtle bg-surface-1 pt-4 mt-4">
            <button
              type="submit"
              className="h-9 rounded-lg bg-primary px-4 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-primary/20"
            >
              {t('projectConfigGear.done')}
            </button>
          </div>}
        </form>
      </ProjectSettingsSurface>

      {config && (
        <StyleLibraryModal
          isOpen={isStyleLibraryOpen}
          onClose={() => {
            log('style:modal_close', { applied: false })
            setIsStyleLibraryOpen(false)
          }}
          currentStyleId={config.style_id}
          onApply={handleApplyStyle}
        />
      )}
    </>
  )
})
